import React, { useState, useEffect, useMemo, Fragment } from 'react';
import { useSearchParams, useParams } from 'react-router-dom';
import { PAGE_TYPE } from '../../constants/gaEnums';
import { useScrollToTopOnPageChange } from '../../hooks/useScrollToTopOnPageChange';
import { Grid, Box, Divider, useMediaQuery, Stack } from '@mui/material';
import theme from '../../theme';
import PageLayout from '../../components/layout/PageLayout';
import ProgressBar from '../../components/common/ProgressBar';
import Pagination from '../../components/common/Pagination';
import InFeedAd from '../../components/ad/InFeedAd';
import SidebarLayout from '../../components/layout/SidebarLayout';
import ArtistCard from '../../components/card/ArtistCard';
import NewsCard from '../../components/card/NewsCard';
import PlaylistCard from '../../components/card/PlaylistCard';
import PlaylistVerticalCard from '../../components/card/PlaylistVerticalCard';
import NoticeCard from '../../components/card/NoticeCard';
import { searchApi } from '../../api/search';
import SearchResultHeader from './components/SearchResultHeader';
import PageTitle from '../../components/common/PageTitle';
import { categoryFormatter } from '../../utils/formatter';
import AlbumVerticalCard from '../../components/card/AlbumVerticalCard';

// 카테고리별 레이아웃 설정
const layoutConfig = {
  artists: {
    layout: 'grid',
    gridSize: { xs: 6, md: 3 },
    itemsPerPage: { xs: 20, md: 40 },
    itemsPerAd: { xs: 10, md: 20 },
    spacing: {
      grid: {
        rowSpacing: { xs: 3, md: 5, lg: 5 },
        columnSpacing: { xs: 2, md: 2, lg: 2.5 },
      },
    },
  },
  reviews: {
    layout: 'grid',
    gridSize: { xs: 6, md: 3 },
    itemsPerPage: { xs: 20, md: 40 },
    itemsPerAd: { xs: 10, md: 20 },
    spacing: {
      grid: {
        rowSpacing: { xs: 3, md: 5, lg: 5 },
        columnSpacing: { xs: 2, md: 2, lg: 2.5 },
      },
    },
  },
  news: {
    layout: 'stack',
    itemsPerPage: 10,
    itemsPerAd: 5,
    spacing: {
      stack: { xs: 1.875, md: 2.5, lg: 3.75 },
    },
  },
  lists: {
    layout: { xs: 'stack', md: 'grid' },
    gridSize: { md: 3 },
    itemsPerPage: { xs: 10, md: 40 },
    itemsPerAd: { xs: 5, md: 20 },
    spacing: {
      grid: {
        rowSpacing: 5,
        columnSpacing: { md: 2, lg: 2.5 },
      },
      stack: 1.875,
    },
  },
  mixes: {
    layout: { xs: 'stack', md: 'grid' },
    gridSize: { md: 3 },
    itemsPerPage: { xs: 10, md: 40 },
    itemsPerAd: { xs: 5, md: 20 },
    spacing: {
      grid: {
        rowSpacing: 5,
        columnSpacing: { md: 2, lg: 2.5 },
      },
      stack: 1.875,
    },
  },
  notices: {
    layout: 'stack',
    itemsPerPage: 10,
    itemsPerAd: 5,
    spacing: {
      stack: { xs: 1.875, md: 2.5, lg: 3.75 },
    },
  },
};

const SearchResultDetail = () => {
  const { category } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get('q');
  const urlPage = parseInt(searchParams.get('page') || '1', 10);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(urlPage);
  const [totalPages, setTotalPages] = useState(1);

  const isXs = useMediaQuery(theme.breakpoints.down('md'));
  const isLg = useMediaQuery(theme.breakpoints.up('lg'));
  const config = layoutConfig[category] || {};

  // 현재 레이아웃 타입 (grid or stack)
  const currentLayout =
    typeof config.layout === 'object'
      ? isXs
        ? config.layout.xs
        : config.layout.md
      : config.layout;
  const isGridLayout = currentLayout === 'grid';

  // 반응형 값 처리
  const itemsPerPage =
    typeof config.itemsPerPage === 'object'
      ? isXs
        ? config.itemsPerPage.xs
        : config.itemsPerPage.md
      : config.itemsPerPage || 10;
  const itemsPerAd =
    typeof config.itemsPerAd === 'object'
      ? isXs
        ? config.itemsPerAd.xs
        : config.itemsPerAd.md
      : config.itemsPerAd || 5;

  useEffect(() => {
    setPage(urlPage);
  }, [urlPage]);

  useEffect(() => {
    if (!searchParams.get('page')) {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('page', '1');
      setSearchParams(newSearchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useScrollToTopOnPageChange();

  const safePage = totalPages > 0 ? Math.min(page, totalPages) : 1;

  const currentPageItems = useMemo(() => {
    const startIndex = (safePage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return items.slice(startIndex, endIndex);
  }, [items, safePage, itemsPerPage]);

  useEffect(() => {
    const fetchSearchResults = async () => {
      try {
        const apiCategory = category === 'mixes' ? 'lists' : category;
        const { data } = await searchApi.searchCategory(apiCategory, query);
        setItems(data);
      } catch (err) {
        console.error('Error fetching search results:', err);
      } finally {
        setLoading(false);
      }
    };

    if (category) {
      fetchSearchResults();
    }
  }, [category, query]);

  useEffect(() => {
    setTotalPages(Math.ceil(items.length / itemsPerPage));
  }, [items, itemsPerPage]);

  const getCardComponent = (item) => {
    switch (category) {
      case 'artists':
        return (
          <ArtistCard
            id={item.id}
            name={item.name}
            artistImageUrl={item.artistImageUrl}
            artistSource={item.artistSource}
            slug={item.slug}
            artistDbId={item.artistId}
          />
        );
      case 'reviews':
        return (
          <AlbumVerticalCard
            albumId={item.albumId}
            sourceId={item.sourceId}
            albumSource={item.albumSource}
            slug={item.slug}
            imageUrl={item.albumImageUrl}
            albumName={item.title}
            contentRating={item.contentRating}
            artistName={item.artist.name}
            artistId={item.artist.sourceId}
            artistDbId={item.artist.id}
            artistSource={item.artist.artistSource}
            artistSlug={item.artist.slug}
            releaseDate={item.releaseDate}
            albumAvgRating={item.averageRating}
            commentCount={item.commentCount}
            bookmarkCount={item.bookmarkCount}
            isStarred={item.isStarred}
            hideBookmarkCount
            ratingSolo={!isLg}
            dateSolo={isLg}
          />
        );
      case 'news':
        return <NewsCard news={item} showContent />;
      case 'lists':
      case 'mixes':
        return isGridLayout ? (
          <PlaylistVerticalCard playlist={item} thumbnailVariant="square" />
        ) : (
          <PlaylistCard playlist={item} dateOnNewLine thumbnailVariant="square" />
        );
      case 'notices':
        return <NoticeCard notice={item} />;
      default:
        return null;
    }
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('page', newPage.toString());
    setSearchParams(newSearchParams, { replace: false });
  };

  const getSpacing = () => (isGridLayout ? config.spacing?.grid : config.spacing?.stack);

  const renderContent = () => {
    const spacing = getSpacing();

    if (isGridLayout) {
      return (
        <Box sx={{ overflow: 'hidden' }}>
          <Grid container {...spacing}>
            {currentPageItems.map((item, index) => (
              <Fragment key={item.id || item.sourceId}>
                <Grid item {...config.gridSize}>
                  {getCardComponent(item)}
                </Grid>
                {(index + 1) % itemsPerAd === 0 && index < currentPageItems.length - 1 && (
                  <Grid item xs={12}>
                    <InFeedAd pageType={PAGE_TYPE.SEARCH_MORE} />
                  </Grid>
                )}
              </Fragment>
            ))}
          </Grid>
        </Box>
      );
    }

    return (
      <Stack spacing={spacing}>
        {currentPageItems.map((item, index) => (
          <Fragment key={item.id || item.sourceId}>
            <Box>
              {getCardComponent(item)}
              <Divider sx={{ borderColor: theme.palette.grey.line, mt: spacing }} />
            </Box>
            {(index + 1) % itemsPerAd === 0 && index < currentPageItems.length - 1 && (
              <InFeedAd pageType={PAGE_TYPE.SEARCH_MORE} />
            )}
          </Fragment>
        ))}
      </Stack>
    );
  };

  return (
    <PageLayout>
      <ProgressBar isLoading={loading}>
        <SearchResultHeader query={query} />
        <PageTitle mb={{ xs: 2, md: 2.5, lg: 3.75 }}>{categoryFormatter(category)}</PageTitle>
        <SidebarLayout pageType={PAGE_TYPE.SEARCH_MORE}>
          {renderContent()}
          {totalPages > 1 && (
            <Pagination count={totalPages} page={safePage} onChange={handlePageChange} />
          )}
        </SidebarLayout>
      </ProgressBar>
    </PageLayout>
  );
};

export default SearchResultDetail;
